import React from 'react';
import Tooltip, { DefaultTooltipPopperProps } from 'js/components/elements/mui/Tooltip';
import styles from './SemanticSensitivityBadge.module.scss';
import { ReactComponent as GenAiIcon } from 'new/components/icons/genai.svg';
import { ReactComponent as PublicIcon } from 'new/components/icons/fi-rr-globe.svg';
import { ReactComponent as ConfidentialIcon } from 'new/components/icons/fi-rr-lock.svg';
import { ReactComponent as InternalIcon } from 'new/components/icons/fi-rr-building.svg';
import { ReactComponent as RestrictedIcon } from 'new/components/icons/fi-rr-warning.svg';
import { sortObjectEntries } from 'new/utils/sortObjectEntries';
import cx from 'classnames';

// Define preferred order of keys
const SEMANTIC_INFO_PREFERRED_KEYS = ['dataset_sensitivity', 'reason', 'dataset_name'];

interface SensitivityInfo {
  label: string;
  icon: React.ReactNode;
}

const sensitivityMap: Record<string, SensitivityInfo> = {
  SecretRestricted: {
    label: 'Secret/Restricted',
    icon: <RestrictedIcon />,
  },
  Confidential: {
    label: 'Confidential',
    icon: <ConfidentialIcon />,
  },
  Internal: {
    label: 'Internal',
    icon: <InternalIcon />,
  },
  Public: {
    label: 'Public',
    icon: <PublicIcon />,
  },
};

type Size = 'sm' | 'md' | 'lg';

interface SemanticSensitivityBadgeProps {
  semanticInfo: Record<string, unknown>;
  size?: Size;
}

// Format the property name for display
const formatPropertyName = (key: string): string => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatValue = (value: unknown): React.ReactNode => {
  if (!value) return null;

  if (Array.isArray(value)) {
    if (value.length === 1) {
      return String(value[0]);
    }

    return (
      <div className={styles.bulletList}>
        {value.map((item, index) => (
          <div key={index} className={styles.bulletPoint}>
            <span>•</span>
            <span>{String(item)}</span>
          </div>
        ))}
      </div>
    );
  }

  return String(value);
};

interface TooltipContentProps {
  semanticInfo: Record<string, unknown>;
}

const TooltipContent = ({ semanticInfo }: TooltipContentProps) => {
  const sortedSemanticInfo = semanticInfo
    ? sortObjectEntries(Object.entries(semanticInfo), SEMANTIC_INFO_PREFERRED_KEYS)
    : [];

  return (
    <div className={styles.tooltipContent}>
      {sortedSemanticInfo.map(([key, value]) => {
        if (!value || key === 'dataset_name') return null;
        return (
          <div key={key} className={styles.tooltipItem}>
            <span>
              <b>{formatPropertyName(key)}:</b> {formatValue(value)}
            </span>
          </div>
        );
      })}
      <div className={styles.tooltipFooter}>
        <GenAiIcon height={12} width={12} />
        <span className={styles.tooltipFooterText}>NOVA Generated Semantic Schema</span>
      </div>
    </div>
  );
};

export function SemanticSensitivityBadge({
  semanticInfo,
  size = 'sm',
}: SemanticSensitivityBadgeProps): React.ReactElement | null {
  if (!semanticInfo) {
    return null;
  }

  const sensitivity = (semanticInfo.sensitivity ?? semanticInfo.dataset_sensitivity ?? '')
    .toString()
    .replace('/', '');

  if (!sensitivity) {
    return null;
  }

  // get the sensitivity info or use default if not found
  const sensitivityInfo = sensitivityMap[sensitivity] || {
    label: sensitivity,
    icon: <GenAiIcon />,
  };

  const tooltipContent = <TooltipContent semanticInfo={semanticInfo} />;

  return (
    <div>
      <Tooltip tooltipTheme="fieldInfo" title={tooltipContent}>
        <div
          className={cx(
            styles['semantic-sensitivity'],
            styles[`sensitivity-${sensitivity}`],
            styles[`size-${size}`],
          )}
        >
          {sensitivityInfo.icon}
          <span>{sensitivityInfo.label}</span>
        </div>
      </Tooltip>
    </div>
  );
}
